import React, { useState } from 'react'
import { Container } from 'reactstrap'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import IssuesList from './IssuesList'
import localesActions from '../../../localez/actions'
import PageHeader from '../../../components/PageHeader'
import projectActions from '../actions'
import projectStringsAlertsActions from '../ProjectStringsAlerts/actions'

const ImportIssues = ({
  bulkUpdateStatus = 'idle',
  dispatch,
  locales = [],
  localesFetchingStatus = 'idle',
  localeSuggestions = [],
  location: { pathname = null, search } = {},
  match: { params: { slug = null } = {} } = {},
  project = null,
  projectFetchingStatus = 'idle',
  projectStringsAlerts = null,
  projectStringsAlertsCurrentPage = null,
  projectStringsAlertsFetchingStatus = 'idle',
  projectStringsAlertsFilteringStatus = 'idle',
  projectStringsAlertsTotalCount = null,
  projectStringsAlertsTotalPages = null
}) => {
  const { t } = useTranslation('ProjectsPage')
  const pageParams = new URLSearchParams(search)
  const page = pageParams.get('page')

  const [currentPage, setCurrentPage] = useState(page || null)

  const buildPageParams = (params = {}) => {
    let nextSearch = ''

    if (Object.keys(params).length > 0
        && Object.values(params).every((value) => value === null) === false
    ) {
      nextSearch = new URLSearchParams({
        page: params.page || currentPage
      })
    }

    return nextSearch.toString()
  }

  const updatePageUrl = (searchParams) => (
    window.history.pushState(
      null,
      '',
      `${pathname}?${buildPageParams(searchParams)}`
    )
  )

  const fetchAll = (params = {}) => {
    dispatch(projectStringsAlertsActions.fetchAll(
      slug,
      params.page || currentPage
    ))

    updatePageUrl({ page: params.page || currentPage })
  }

  const moveToPage = (selected) => {
    setCurrentPage(selected)
    fetchAll({ page: selected })
  }

  const handlePathSearch = (pathPattern) => {
    if (pathPattern) {
      dispatch(projectStringsAlertsActions.filter(
        slug,
        { column: 'path', value: pathPattern },
        1
      ))
    } else {
      fetchAll({ page: 1 })
    }
  }

  const handleBulkApply = (pathPattern, locale) => {
    dispatch(projectStringsAlertsActions.bulkAssignLocale(slug, pathPattern, locale))
  }

  const handleBulkApplyAll = (suggestions) => {
    // Apply each suggestion sequentially
    suggestions.forEach((suggestion) => {
      dispatch(projectStringsAlertsActions.bulkAssignLocale(
        slug,
        suggestion.path_pattern,
        suggestion.locale
      ))
    })
  }

  if (projectFetchingStatus === 'idle') dispatch(projectActions.fetch(slug))
  if (localesFetchingStatus === 'idle') dispatch(localesActions.fetchAll())
  if (projectStringsAlertsFetchingStatus === 'idle') fetchAll()

  return (
    <Container fluid className="d-flex flex-column flex-fill mt-3">
      <PageHeader
        projectSlug={slug}
        title={t('ImportIssues.title')}
      />

      <div className="d-flex flex-column flex-fill mt-3">
        <IssuesList
          bulkUpdateStatus={bulkUpdateStatus}
          fetchingStatus={projectStringsAlertsFetchingStatus}
          filteringStatus={projectStringsAlertsFilteringStatus}
          items={projectStringsAlerts}
          locales={locales}
          localeSuggestions={localeSuggestions}
          localesFetchingStatus={localesFetchingStatus}
          moveToPage={(selected) => moveToPage(selected)}
          onBulkApply={handleBulkApply}
          onBulkApplyAll={handleBulkApplyAll}
          onPathSearch={handlePathSearch}
          paginationCurrentPage={projectStringsAlertsCurrentPage}
          paginationTotalPages={projectStringsAlertsTotalPages}
          projectSlug={slug}
          totalCount={projectStringsAlertsTotalCount}
        />
      </div>
    </Container>
  )
}

ImportIssues.propTypes = {
  bulkUpdateStatus: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  locales: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  })),
  localesFetchingStatus: PropTypes.string,
  localeSuggestions: PropTypes.arrayOf(PropTypes.shape({
    count: PropTypes.number.isRequired,
    locale: PropTypes.string.isRequired,
    path_pattern: PropTypes.string.isRequired
  })),
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      slug: PropTypes.string
    })
  }),
  project: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  }),
  projectFetchingStatus: PropTypes.string,
  projectStringsAlerts: PropTypes.arrayOf(PropTypes.shape()),
  projectStringsAlertsCurrentPage: PropTypes.number,
  projectStringsAlertsFetchingStatus: PropTypes.string,
  projectStringsAlertsFilteringStatus: PropTypes.string,
  projectStringsAlertsTotalCount: PropTypes.number,
  projectStringsAlertsTotalPages: PropTypes.number
}

const mapStateToProps = ({
  locales: {
    fetchingStatus: localesFetchingStatus,
    items: locales
  },
  projects: {
    fetchingStatus: projectFetchingStatus,
    item: project
  },
  projectStringsAlerts: {
    bulkUpdateStatus,
    fetchingStatus: projectStringsAlertsFetchingStatus,
    filteringStatus: projectStringsAlertsFilteringStatus,
    itemsCurrentPage: projectStringsAlertsCurrentPage,
    items: projectStringsAlerts,
    itemsTotalCount: projectStringsAlertsTotalCount,
    itemsTotalPages: projectStringsAlertsTotalPages,
    localeSuggestions
  }
}) => ({
  bulkUpdateStatus,
  locales,
  localesFetchingStatus,
  localeSuggestions,
  project,
  projectFetchingStatus,
  projectStringsAlerts,
  projectStringsAlertsCurrentPage,
  projectStringsAlertsFetchingStatus,
  projectStringsAlertsFilteringStatus,
  projectStringsAlertsTotalCount,
  projectStringsAlertsTotalPages
})

export default connect(mapStateToProps)(ImportIssues)
